import React, { useState, useMemo } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Avatar,
  Typography,
  InputAdornment,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { CurrentUserInfoDTO } from '../../api/generated/requests/types.gen';

interface UserSearchSelectProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  onBlur?: () => void;
  users?: CurrentUserInfoDTO[];
  isLoading?: boolean;
  label?: string;
  helperText?: string;
  error?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  getUserDisplayName: (userId: string | undefined) => string;
}

const UserSearchSelect: React.FC<UserSearchSelectProps> = ({
  value,
  onChange,
  onBlur,
  users = [],
  isLoading = false,
  label = "Przypisane do",
  helperText,
  error = false,
  size = 'small',
  fullWidth = true,
  getUserDisplayName
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) {
      return users;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return users.filter(user => {
      const displayName = getUserDisplayName(user.id);
      const firstName = user.firstName?.toLowerCase() || '';
      const lastName = user.lastName?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';
      const login = user.login?.toLowerCase() || '';
      
      return displayName.toLowerCase().includes(searchLower) ||
             firstName.includes(searchLower) ||
             lastName.includes(searchLower) ||
             email.includes(searchLower) ||
             login.includes(searchLower);
    });
  }, [users, searchTerm, getUserDisplayName]);

  const handleOpen = () => {
    setOpen(true);
    setSearchTerm('');
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleChange = (event: any) => {
    const selectedValue = event.target.value;
    onChange(selectedValue === '' ? undefined : selectedValue);
    handleClose();
  };

  const renderValue = (selected: any) => {
    if (!selected) {
      return <em>Nieprzypisane</em>;
    }
    return getUserDisplayName(selected);
  };

  const getUserInitials = (user: CurrentUserInfoDTO) => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <FormControl size={size} fullWidth={fullWidth} error={error}>
      <InputLabel id="user-search-select-label">{label}</InputLabel>
      <Select
        labelId="user-search-select-label"
        value={value || ''}
        label={label}
        onChange={handleChange}
        onBlur={onBlur}
        onOpen={handleOpen}
        onClose={handleClose}
        open={open}
        renderValue={renderValue}
        startAdornment={
          isLoading ? (
            <InputAdornment position="start">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null
        }
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 400,
            },
          },
        }}
      >
        {/* Search field */}
        <Box sx={{ p: 1, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Szukaj użytkownika..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </Box>

        {/* No assignment option */}
        <MenuItem value="">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 28, height: 28, bgcolor: 'grey.300', fontSize: '0.75rem' }}>
              <PersonIcon fontSize="small" />
            </Avatar>
            <Typography fontSize="0.8rem">
              <em>Nieprzypisane</em>
            </Typography>
          </Box>
        </MenuItem>

        {/* Loading state */}
        {isLoading && (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography fontSize="0.8rem">Ładowanie użytkowników...</Typography>
            </Box>
          </MenuItem>
        )}

        {/* No results */}
        {!isLoading && searchTerm && filteredUsers.length === 0 && (
          <MenuItem disabled>
            <Typography fontSize="0.8rem" color="text.secondary">
              Brak wyników dla "{searchTerm}"
            </Typography>
          </MenuItem>
        )}

        {/* Filtered users */}
        {!isLoading && filteredUsers.map((user) => (
          <MenuItem key={user.id} value={user.id || ''}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {user.avatarUrl ? (
                <Avatar 
                  src={user.avatarUrl}
                  sx={{ 
                    width: 28, 
                    height: 28,
                    border: '1px solid',
                    borderColor: 'primary.main',
                  }}
                />
              ) : (
                <Avatar sx={{ width: 28, height: 28, bgcolor: user.color || 'primary.main', fontSize: '0.75rem' }}>
                  {getUserInitials(user) || <PersonIcon fontSize="small" />}
                </Avatar>
              )}
              <Typography fontSize="0.8rem">{getUserDisplayName(user.id)}</Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
      {helperText && (
        <FormHelperText>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default UserSearchSelect;